import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Refresh, Timer } from '@mui/icons-material';

interface RefreshPodsPanelProps {
  onRefresh: () => void;
  isRefreshing: boolean;
}

// Configurable refresh interval in seconds
const REFRESH_INTERVAL_SECONDS = 30;

export const K8sAnalyzerRefreshPodsPanel: React.FC<RefreshPodsPanelProps> = ({
  onRefresh,
  isRefreshing
}) => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_SECONDS);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Setup auto-refresh with countdown
  useEffect(() => {
    const startCountdown = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      
      setCountdown(REFRESH_INTERVAL_SECONDS);
      
      refreshTimerRef.current = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            onRefresh(); // Silent refresh
            return REFRESH_INTERVAL_SECONDS; // Reset countdown
          }
          return prevCount - 1;
        });
      }, 1000);
    };

    if (autoRefresh) {
      startCountdown();
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, onRefresh]);

  const handleManualRefresh = () => {
    onRefresh();
    
    if (autoRefresh) {
      // Reset the countdown after manual refresh
      setCountdown(REFRESH_INTERVAL_SECONDS);
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => !prev);
  };

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        padding: '4px 12px',
        backgroundColor: theme => autoRefresh ? theme.palette.primary.main : 'transparent',
        color: theme => autoRefresh ? theme.palette.primary.contrastText : theme.palette.text.primary
      }}>
        <Timer fontSize="small" />
        <Typography variant="body2">
          {autoRefresh ? `Auto-refresh in ${countdown}s` : "Auto-refresh off"}
        </Typography>
      </Box>
      
      <Button
        variant="outlined"
        color={autoRefresh ? "primary" : "inherit"}
        onClick={toggleAutoRefresh}
        sx={{ textTransform: 'none' }}
      >
        {autoRefresh ? 'Disable Auto-refresh' : 'Enable Auto-refresh'}
      </Button>
      
      <Button
        startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
        variant="contained"
        color="primary"
        onClick={handleManualRefresh}
        disabled={isRefreshing}
        sx={{ textTransform: 'none' }}
      >
        {isRefreshing ? 'REFRESHING...' : 'REFRESH'}
      </Button>
    </>
  );
};

export default K8sAnalyzerRefreshPodsPanel; 